import React, { useState } from 'react';
import { IoClose } from 'react-icons/io5';
import { DeadlineType } from '../index';

interface AddDeadlineModalProps {
  onClose: () => void;
  getTypeIcon: (type: DeadlineType) => JSX.Element;
  getTypeColor: (type: DeadlineType) => string;
}

const AddDeadlineModal: React.FC<AddDeadlineModalProps> = ({
  onClose,
  getTypeIcon,
  getTypeColor,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'application' as DeadlineType,
    description: '',
    dueDate: '',
    dueTime: '23:59',
    estimatedHours: '',
    estimatedMinutes: '',
    priority: 'medium' as 'high' | 'medium' | 'low',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // In a real app, this would save to backend
    console.log('Adding deadline:', {
      ...formData,
      estimatedTimeHours: parseFloat(formData.estimatedHours || '0') + parseFloat(formData.estimatedMinutes || '0') / 60,
    });
    
    // Show success message
    alert('Custom deadline added successfully!');
    onClose();
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">Add Custom Deadline</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <IoClose />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="deadline-form">
          <div className="form-group">
            <label className="form-label">Task Name *</label>
            <input
              type="text"
              className="form-input"
              placeholder="Enter task name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Type *</label>
            <select
              className="form-select"
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              required
            >
              <option value="application">Application Deadline</option>
              <option value="scholarship">Scholarship Deadline</option>
              <option value="opportunity">Important Opportunity</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              placeholder="Enter description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              rows={3}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Due Date *</label>
              <input
                type="date"
                className="form-input"
                value={formData.dueDate}
                onChange={(e) => handleInputChange('dueDate', e.target.value)}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Due Time</label>
              <input
                type="time"
                className="form-input"
                value={formData.dueTime}
                onChange={(e) => handleInputChange('dueTime', e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Estimated Time (Optional)</label>
            <div className="time-inputs">
              <input
                type="number"
                className="form-input"
                placeholder="Hours"
                min="0"
                value={formData.estimatedHours}
                onChange={(e) => handleInputChange('estimatedHours', e.target.value)}
              />
              <input
                type="number"
                className="form-input"
                placeholder="Minutes"
                min="0"
                max="59"
                value={formData.estimatedMinutes}
                onChange={(e) => handleInputChange('estimatedMinutes', e.target.value)}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Priority</label>
            <select
              className="form-select"
              value={formData.priority}
              onChange={(e) => handleInputChange('priority', e.target.value)}
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
            </select>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              Add Deadline
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDeadlineModal;